import type React from 'react';
import { type CSSProperties, forwardRef, type PropsWithChildren } from 'react';
import styled, { css } from 'styled-components';
import { TruncateText } from '@/components/TruncateText';
import type { DataCyProp } from '@/types';
import { TypographySize } from '../enums/size';
import { TypographyType } from '../enums/type';
import type { StaticOptions } from '../types/StaticOptions';
import { getTypographyTypeColor } from '../utils/getTypographyTypeColor';

export interface ParagraphProps extends DataCyProp {
  bold?: boolean;
  size?: TypographySize.Large | TypographySize.Medium;
  type?: TypographyType.Normal | TypographyType.Secondary | TypographyType.Inverted | TypographyType.Warning;
  isSpaced?: boolean;
  /**
   * When used in static context, staticOptions must be provided to the component
   *  */
  staticOptions?: StaticOptions;
  /**
   * Maximum number of characters to display. If exceeded, text will be truncated with an ellipsis.
   */
  maxLength?: number;
  style?: CSSProperties;
  key?: React.Key;
  fitContent?: boolean;
}

export const StyledParagraph = styled.span<Pick<ParagraphProps, 'bold' | 'size' | 'type' | 'isSpaced' | 'fitContent'>>`
  margin: 0;
  white-space: normal;
  word-break: break-word;
  width: 100%;

  ${({ fitContent }) =>
    fitContent &&
    css`
      width: fit-content;
  `};

  ${({ theme, size }) =>
    size === TypographySize.Large
      ? theme.typography.configurations.paragraphLarge
      : theme.typography.configurations.paragraphMedium};

  ${({ type, theme }) => {
    return css`
      color: ${getTypographyTypeColor(type, theme)};
    `;
  }};

  ${({ isSpaced: spaced }) =>
    spaced &&
    css`
      letter-spacing: 0.5px;
    `}

     ${({ bold }) =>
       bold &&
       css`
      font-weight: 600;
    `};
`;

export const Paragraph = forwardRef<HTMLSpanElement, PropsWithChildren<ParagraphProps>>(
  (
    {
      children,
      size = TypographySize.Medium,
      isSpaced = false,
      bold = false,
      type = TypographyType.Normal,
      staticOptions,
      maxLength,
      style,
      'data-cy': dataCy,
      key,
      fitContent = false,
    },
    ref,
  ) => {
    const { theme } = staticOptions ?? {};
    const isString = typeof children === 'string';
    const shouldTruncate = isString && maxLength !== undefined;

    return (
      <StyledParagraph
        data-cy={dataCy}
        fitContent={fitContent}
        isSpaced={isSpaced}
        key={key}
        ref={ref}
        size={size}
        bold={bold}
        style={style}
        theme={theme}
        type={type}
      >
        {shouldTruncate ? <TruncateText maxLength={maxLength} text={children as string} truncateEnd /> : children}
      </StyledParagraph>
    );
  },
);
